import React from 'react'
import { DateInput } from 'semantic-ui-calendar-react'
import { Form, Header } from 'semantic-ui-react'
import { CharacterCountInput } from '../../components/CharacterCountInput'
import HelperTextLabel from '../../components/HelperTextLabel'
import { isDateBeforeToday } from '../../utils'
import stringToHHMMSS from '../../utils/stringToHHMMSS'
import { getError } from './helpers'
import {
  capacityOptions,
  CurrencyOptions,
  helpText,
  InstrumentTypeOptions,
  ListingCountryTypeOptions,
  securityCodeTypeOptions,
  SettlementTypeOptions,
  TradeActionOptions,
  TradeActionScenario,
  TradeTypeOptions,
} from './constants'
import {
  AccountTypeEnum,
  InstrumentTypeEnum,
  SettlementTypeEnum,
  TradeFormErrors,
  TradeFormTouched,
  TradeFormValues,
} from './Types'

export interface FormDetailsProps {
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
  handleChange: (e: React.ChangeEvent) => void
  handleBlur: (field: string) => void
  values: TradeFormValues
  errors: TradeFormErrors
  touched: TradeFormTouched
}

function FormDetails({ setFieldValue, handleChange, handleBlur, values, errors, touched }: FormDetailsProps) {
  const today = new Date()
  const tradeDateBeforeToday = isDateBeforeToday(values.tradeDate)
  const settlementDateRequired = values.settlementType === SettlementTypeEnum.specialSettlement || tradeDateBeforeToday
  const clearCounterpartyValues = () => {
    setFieldValue('routeType', undefined)
    setFieldValue('orderId', undefined)
    setFieldValue('executionAlgorithm', undefined)
    setFieldValue('sourceAccountNumber', undefined)
    setFieldValue('sourceAccountType', undefined)
    setFieldValue('executingBroker', undefined)
    setFieldValue('market', undefined)
  }
  let tradeActionOptions
  if (values.instrumentType === InstrumentTypeEnum.option) {
    tradeActionOptions = TradeActionOptions[TradeActionScenario.OptionInstrumentType]
  } else if (values.accountType === AccountTypeEnum.Short) {
    tradeActionOptions = TradeActionOptions[TradeActionScenario.ShortAccountTypeNonOptionInstrumentType]
  } else {
    tradeActionOptions = TradeActionOptions[TradeActionScenario.NotShortAccountTypeNonOptionInstrumentType]
  }
  return (
    <>
      <Header as={'h2'}>Trade Details</Header>
      <Form>
        <Form.Group widths="equal">
          <Form.Select
            className={'test-security-code-type'}
            selection={true}
            required={true}
            search={true}
            label="Security Code Type"
            name="securityCodeType"
            value={values.securityCodeType}
            options={securityCodeTypeOptions}
            onChange={(e: React.SyntheticEvent, data) => {
              setFieldValue('securityCodeType', data.value)
            }}
            onBlur={() => {
              handleBlur('securityCodeType')
            }}
          />
          <Form.Input
            className={'test-symbol'}
            label={<HelperTextLabel label={'Symbol'} helperText={helpText.symbol} />}
            name="symbol"
            value={values.symbol}
            onChange={handleChange}
            onBlur={() => {
              setFieldValue('symbol', values.symbol.trim())
              handleBlur('symbol')
            }}
            required={true}
            error={getError('symbol', errors, touched)}
          />
          <Form.Select
            selection={true}
            search={true}
            required={true}
            label="Action"
            name="action"
            value={values.action}
            options={tradeActionOptions}
            onChange={(e: React.SyntheticEvent, data) => {
              setFieldValue('action', data.value)
            }}
            onBlur={() => {
              handleBlur('action')
            }}
            error={getError('action', errors, touched)}
          />
          <Form.Input
            className={'test-quantity'}
            label="Quantity"
            name="quantity"
            type="number"
            value={values.quantity}
            onChange={handleChange}
            required={true}
            error={getError('quantity', errors, touched)}
            onBlur={() => {
              handleBlur('quantity')
            }}
          />
          <Form.Input
            className={'test-execution-price'}
            label="Execution Price"
            name="executionPrice"
            type="number"
            value={values.executionPrice}
            onChange={handleChange}
            onBlur={() => {
              handleBlur('executionPrice')
            }}
            required={true}
            error={getError('executionPrice', errors, touched)}
          />
        </Form.Group>
        <Form.Group widths="equal">
          <Form.Select
            selection={true}
            required={true}
            search={true}
            label="Capacity"
            name="capacity"
            value={values.capacity}
            options={capacityOptions}
            onChange={(e: React.SyntheticEvent, data) => {
              setFieldValue('capacity', data.value)
            }}
            onBlur={() => {
              handleBlur('capacity')
            }}
            error={getError('capacity', errors, touched)}
          />
          <Form.Select
            className={'test-instrument-type'}
            selection={true}
            required={true}
            search={true}
            label="Instrument Type"
            name="instrumentType"
            value={values.instrumentType}
            options={InstrumentTypeOptions}
            onChange={(e: React.SyntheticEvent, data) => {
              setFieldValue('instrumentType', data.value)
              setFieldValue('action', '')
            }}
            onBlur={() => {
              handleBlur('instrumentType')
            }}
          />
          <Form.Select
            className={'test-trade-type'}
            selection={true}
            required={true}
            search={true}
            label="Trade Type"
            name="tradeType"
            value={values.tradeType}
            options={TradeTypeOptions}
            onChange={(e: React.SyntheticEvent, data) => {
              setFieldValue('tradeType', data.value)
              clearCounterpartyValues()
            }}
            onBlur={() => {
              handleBlur('tradeType')
            }}
          />
          <Form.Select
            className={'test-listing-country'}
            selection={true}
            required={true}
            search={true}
            label="Listing Country"
            name="listingCountry"
            value={values.listingCountry}
            options={ListingCountryTypeOptions}
            onChange={(e, data) => {
              setFieldValue('listingCountry', data.value)
            }}
            onBlur={() => {
              handleBlur('listingCountry')
            }}
          />
          <Form.Select
            className={'test-currency'}
            selection={true}
            required={true}
            search={true}
            label="Currency"
            name="currency"
            value={values.currency}
            options={CurrencyOptions}
            onChange={(e, data) => {
              setFieldValue('currency', data.value)
            }}
            onBlur={() => {
              handleBlur('currency')
            }}
          />
        </Form.Group>
        <Form.Group widths="equal">
          <DateInput
            className={'test-trade-date'}
            error={getError('tradeDate', errors, touched)}
            name="tradeDate"
            label="Trade Date"
            iconPosition="right"
            required={true}
            dateFormat="MM/DD/YYYY"
            placeholder={'MM/DD/YYYY'}
            value={values.tradeDate}
            maxDate={today}
            onChange={(e, data) => {
              setFieldValue('tradeDate', data.value)
            }}
            onBlur={() => {
              handleBlur('tradeDate')
            }}
          />
          <Form.Input
            className={'test-trade-time'}
            name="tradeTime"
            label="Trade Time (Eastern Time)"
            placeholder={'HH:MM:SS'}
            value={values.tradeTime}
            onChange={(e, data) => {
              setFieldValue('tradeTime', stringToHHMMSS(data.value))
            }}
            onBlur={() => {
              handleBlur('tradeTime')
            }}
            error={getError('tradeTime', errors, touched)}
          />
          <Form.Select
            className={'test-settlement-type'}
            selection={true}
            required={true}
            search={true}
            label="Settlement Type"
            name="settlementType"
            value={values.settlementType}
            options={SettlementTypeOptions}
            onChange={(e, data) => {
              setFieldValue('settlementType', data.value)
            }}
            onBlur={() => {
              handleBlur('settlementType')
            }}
          />
          <DateInput
            className={'test-settlement-date'}
            name="settlementDate"
            label="Settlement Date"
            required={settlementDateRequired}
            disabled={!settlementDateRequired && !values.settlementDate}
            iconPosition="right"
            dateFormat="MM/DD/YYYY"
            placeholder={'MM/DD/YYYY'}
            value={values.settlementDate}
            minDate={values.tradeDate}
            onChange={(e, data) => {
              setFieldValue('settlementDate', data.value)
            }}
            onBlur={() => {
              handleBlur('settlementDate')
            }}
            error={getError('settlementDate', errors, touched)}
          />
          <CharacterCountInput
            className={'test-memo'}
            label="Memo"
            name="memo"
            value={values.memo}
            onChange={handleChange}
            maxCharacters={20}
            onBlur={() => {
              handleBlur('memo')
            }}
            error={getError('memo', errors, touched)}
          />
        </Form.Group>
      </Form>
    </>
  )
}

export default FormDetails
